import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Receipt, ArrowClockwise, ShieldCheck, Check, X, Printer, Trash } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { openPrintWindow } from "../lib/printWindow";
import PinPad from "../components/PinPad";
import OpenBreaksWidget from "../components/OpenBreaksWidget";

const STATUS_STYLES = {
  pendiente: "bg-yellow-100 text-yellow-700 border-yellow-300",
  preparando: "bg-blue-100 text-blue-700 border-blue-300",
  servido: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelado: "bg-red-100 text-red-700 border-red-300",
};

const isHistory = (s) => s === "servido" || s === "cancelado";

const escapeHtml = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const buildTicketHtml = (o) => {
  const lines = (o.items || []).map((i) => `<tr>
    <td>${i.quantity}× ${escapeHtml(i.name)}${i.variant ? ` <small>(${escapeHtml(i.variant)})</small>` : ""}</td>
    <td class="right">${(i.price * i.quantity).toFixed(2)} €</td>
  </tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Pedido ${escapeHtml(o.customer_name)}</title>
<style>
  @page { size: 72mm auto; margin: 4mm; }
  body { font-family: -apple-system, "Segoe UI", monospace; font-size: 13px; color: #000; padding: 4px; }
  h1 { font-size: 15px; margin: 0 0 4px; text-align: center; }
  .sub { font-size: 11px; text-align: center; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  td { padding: 3px 0; border-bottom: 1px dashed #ccc; }
  td.right { text-align: right; font-variant-numeric: tabular-nums; }
  .total { margin-top: 8px; border-top: 2px solid #000; padding-top: 4px; font-weight: bold; display: flex; justify-content: space-between; }
  .note { margin-top: 6px; font-style: italic; font-size: 11px; }
  .foot { margin-top: 10px; text-align: center; font-size: 10px; color: #666; }
</style></head><body>
  <h1>${escapeHtml(o.customer_name)}${o.table_number ? ` · Mesa ${escapeHtml(o.table_number)}` : ""}</h1>
  <div class="sub">${new Date(o.created_at).toLocaleString("es-ES")}</div>
  <table><tbody>${lines}</tbody></table>
  ${o.notes ? `<div class="note">Nota: ${escapeHtml(o.notes)}</div>` : ""}
  <div class="total"><span>TOTAL</span><span>${(o.total || 0).toFixed(2)} €</span></div>
  <div class="foot">— El Bar Sensenom —</div>
</body></html>`;
};

const printOrder = (o) => {
  const w = openPrintWindow(buildTicketHtml(o), { features: "width=380,height=600" });
  return !!w;
};

export default function PublicOrders() {
  const [pin, setPin] = useState("");
  const [pinRequired, setPinRequired] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(false);
  const [serveTarget, setServeTarget] = useState(null); // {order, workerPin, saving, error}
  const [deleteTarget, setDeleteTarget] = useState(null); // {order, workerPin, saving, error}
  const pinRef = useRef("");
  const printingRef = useRef(new Set());

  // On mount, check if PIN is enabled. If the user came from /fichaje with the
  // gate pin already validated, reuse it automatically to skip the modal.
  useEffect(() => {
    let alive = true;
    const sessionPin = (() => { try { return sessionStorage.getItem("fichaje_access_pin") || ""; } catch (_) { return ""; } })();
    api
      .post("/settings/verify-access-pin", { pin: sessionPin })
      .then((r) => {
        if (!alive) return;
        const enabled = !!r.data.enabled;
        setPinRequired(enabled);
        if (!enabled) {
          setUnlocked(true);
          pinRef.current = "";
          return;
        }
        if (sessionPin && r.data.ok) {
          // Valid gate pin stored in this tab's session → auto-unlock.
          pinRef.current = sessionPin;
          setPin(sessionPin);
          setUnlocked(true);
        }
      })
      .catch(() => {
        if (alive) setPinRequired(true);
      })
      .finally(() => {
        if (alive) setChecking(false);
      });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    if (!unlocked) return;
    setLoading(true);
    try {
      const r = await api.post("/public/orders/list", { pin: pinRef.current });
      const data = r.data || [];
      setOrders(data);
      // Auto-print any unprinted pending order that we're not already handling
      const toPrint = data.filter((o) => o.status === "pendiente" && !o.printed && !printingRef.current.has(o.id));
      for (const o of toPrint) {
        printingRef.current.add(o.id);
        const ok = printOrder(o);
        if (ok) {
          try {
            await api.post(`/public/orders/${o.id}/mark-printed`, { pin: pinRef.current });
          } catch (err) {
            console.warn("mark-printed failed", err);
            printingRef.current.delete(o.id);
          }
        } else {
          printingRef.current.delete(o.id);
          toast.error("El navegador bloquea la impresión automática. Permite pop-ups.");
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [unlocked]);

  useEffect(() => {
    if (!unlocked) return;
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [unlocked, load]);

  const submitServe = async (e) => {
    e.preventDefault();
    if (!serveTarget) return;
    setServeTarget((s) => ({ ...s, saving: true, error: "" }));
    try {
      await api.post(`/public/orders/${serveTarget.order.id}/mark-served`, {
        access_pin: pinRef.current,
        worker_pin: serveTarget.workerPin,
      });
      toast.success("Pedido servido");
      setServeTarget(null);
      load();
    } catch (err) {
      setServeTarget((s) => s && ({ ...s, saving: false, error: formatApiError(err) }));
    }
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    if (!deleteTarget) return;
    setDeleteTarget((s) => ({ ...s, saving: true, error: "" }));
    try {
      await api.post(`/public/orders/${deleteTarget.order.id}/delete`, {
        access_pin: pinRef.current,
        worker_pin: deleteTarget.workerPin,
      });
      toast.success("Pedido eliminado");
      setDeleteTarget(null);
      load();
    } catch (err) {
      setDeleteTarget((s) => s && ({ ...s, saving: false, error: formatApiError(err) }));
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post("/settings/verify-access-pin", { pin });
      if (!r.data.ok) { setError("PIN incorrecto"); return; }
      pinRef.current = pin;
      try { sessionStorage.setItem("fichaje_access_pin", pin); } catch (err) { console.warn("sessionStorage unavailable", err); }
      setUnlocked(true);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--text-secondary)]">Cargando…</div>;
  }

  if (!unlocked && pinRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--bg)]">
        <form onSubmit={submitPin} className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl" data-testid="public-orders-pin-form">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[var(--primary)] flex items-center justify-center">
              <ShieldCheck size={22} weight="duotone" color="#fff" />
            </div>
            <div>
              <div className="label-cap">acceso pedidos</div>
              <h1 className="font-display font-bold text-xl">Introduce el PIN</h1>
            </div>
          </div>
          <PinPad
            value={pin}
            onChange={setPin}
            maxLength={11}
            testIdPrefix="public-orders-pinpad"
            inputTestId="public-orders-pin-input"
            placeholder="••••"
          />
          {error && <div className="text-[var(--danger)] text-sm mt-2 text-center" data-testid="public-orders-pin-error">{error}</div>}
          <button
            type="submit"
            disabled={!pin}
            data-testid="public-orders-pin-submit"
            className="mt-4 w-full py-3 rounded-xl btn-primary font-display font-bold disabled:opacity-40"
          >
            Entrar
          </button>
          <Link to="/fichaje" className="mt-3 flex items-center justify-center gap-2 text-xs text-[var(--text-secondary)] hover:text-[var(--primary)]">
            <ArrowLeft size={14} /> Volver al fichaje
          </Link>
        </form>
      </div>
    );
  }

  const active = orders.filter((o) => !isHistory(o.status));
  const history = orders.filter((o) => isHistory(o.status));
  const pending = orders.filter((o) => o.status === "pendiente").length;
  const visible = tab === "active" ? active : history;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <header className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/fichaje" data-testid="public-orders-back" className="w-10 h-10 rounded-full border border-[var(--border)] bg-white flex items-center justify-center hover:bg-white/80">
              <ArrowLeft size={18} weight="bold" />
            </Link>
            <div className="flex items-center gap-3">
              <Receipt size={26} weight="duotone" className="text-[var(--primary)]" />
              <div>
                <div className="label-cap">terminal camareros</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Pedidos</h1>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pending > 0 && (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--primary)] text-white font-display font-bold text-xs" data-testid="public-orders-pending-badge">
                {pending} pendiente{pending === 1 ? "" : "s"}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              data-testid="public-orders-refresh"
              className="w-10 h-10 rounded-full border border-[var(--border)] bg-white flex items-center justify-center hover:bg-white/80 disabled:opacity-40"
              title="Refrescar"
            >
              <ArrowClockwise size={16} weight="bold" className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </header>

        <OpenBreaksWidget />

        <div className="flex gap-2 mb-6 border-b border-[var(--border)]">
          <button
            onClick={() => setTab("active")}
            data-testid="public-orders-tab-active"
            className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition ${tab === "active" ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--foreground)]"}`}
          >
            Activos <span className="ml-1 text-xs opacity-70">({active.length})</span>
          </button>
          <button
            onClick={() => setTab("history")}
            data-testid="public-orders-tab-history"
            className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition ${tab === "history" ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--foreground)]"}`}
          >
            Histórico <span className="ml-1 text-xs opacity-70">({history.length})</span>
          </button>
        </div>

        {visible.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-[var(--text-secondary)] border border-[var(--border)]" data-testid="public-orders-empty">
            {tab === "active" ? "No hay pedidos activos ahora mismo." : "Aún no hay pedidos en el histórico."}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((o) => (
              <div
                key={o.id}
                data-testid={`public-order-${o.id}`}
                className="bg-white rounded-2xl p-5 flex flex-col border border-[var(--border)] shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-display font-semibold">{o.customer_name}</p>
                    <p className="text-[var(--text-secondary)] text-xs">
                      {o.table_number ? `Mesa ${o.table_number} · ` : ""}
                      {new Date(o.created_at).toLocaleString("es-ES")}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[o.status] || ""}`}
                    data-testid={`public-order-status-${o.id}`}
                  >
                    {o.status}
                  </span>
                </div>
                <ul className="text-sm space-y-1 mb-3 flex-1">
                  {(o.items || []).map((i, idx) => (
                    <li key={`${i.item_id}-${idx}`} className="flex justify-between text-[var(--text-secondary)]">
                      <span>
                        {i.quantity}× {i.name}
                        {i.variant && <span className="text-xs"> ({i.variant})</span>}
                      </span>
                      <span className="font-mono">{(i.price * i.quantity).toFixed(2)} €</span>
                    </li>
                  ))}
                </ul>
                {o.notes && (
                  <p className="text-xs text-[var(--text-secondary)] italic mb-3">Nota: {o.notes}</p>
                )}
                <div className="border-t border-[var(--border)] pt-3 flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-display text-xl text-[var(--primary)] font-bold">{o.total.toFixed(2)} €</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => printOrder(o)}
                      data-testid={`public-order-print-${o.id}`}
                      title="Imprimir ticket"
                      className="w-9 h-9 rounded-full border border-[var(--border)] flex items-center justify-center hover:bg-[var(--bg)]"
                    >
                      <Printer size={15} weight="duotone" />
                    </button>
                    {tab === "active" && (
                      <button
                        onClick={() => setServeTarget({ order: o, workerPin: "", saving: false, error: "" })}
                        data-testid={`public-order-serve-${o.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white font-display font-semibold text-xs hover:bg-emerald-700"
                      >
                        <Check size={14} weight="bold" />
                        Marcar servido
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteTarget({ order: o, workerPin: "", saving: false, error: "" })}
                      data-testid={`public-order-delete-${o.id}`}
                      title="Eliminar pedido erróneo"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white font-display font-semibold text-xs"
                    >
                      <Trash size={14} weight="duotone" />
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-[var(--text-secondary)]">
          Pulsa <strong>Marcar servido</strong> en un pedido activo e introduce tu contraseña. Para gestionarlos por completo (borrar, cambiar estado libre) usa <Link to="/admin/orders" className="underline hover:text-[var(--primary)]">Admin → Pedidos</Link>.
        </p>
      </div>

      {serveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !serveTarget.saving && setServeTarget(null)}>
          <form
            onSubmit={submitServe}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl"
            data-testid="public-order-serve-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-cap">confirmar servicio</div>
                <h3 className="font-display font-bold text-lg">Marcar como servido</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-1">{serveTarget.order.customer_name}{serveTarget.order.table_number ? ` · Mesa ${serveTarget.order.table_number}` : ""} · {serveTarget.order.total.toFixed(2)} €</p>
              </div>
              <button type="button" onClick={() => setServeTarget(null)} disabled={serveTarget.saving} className="p-1 rounded hover:bg-[var(--bg)]" data-testid="public-order-serve-close">
                <X size={18} />
              </button>
            </div>

            <label className="label-cap block mb-2">Introduce tu contraseña</label>
            <PinPad
              value={serveTarget.workerPin}
              onChange={(v) => setServeTarget((s) => ({ ...s, workerPin: v }))}
              maxLength={11}
              testIdPrefix="public-order-serve-pinpad"
              inputTestId="public-order-serve-pin"
              placeholder="••••"
            />
            <p className="text-xs text-[var(--text-secondary)] mt-2">El pedido quedará asignado al trabajador cuya contraseña se introduzca.</p>
            {serveTarget.error && (
              <div className="text-[var(--danger)] text-sm mt-2 text-center" data-testid="public-order-serve-error">{serveTarget.error}</div>
            )}

            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setServeTarget(null)} disabled={serveTarget.saving} className="flex-1 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={serveTarget.saving || !serveTarget.workerPin}
                data-testid="public-order-serve-submit"
                className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-display font-bold disabled:opacity-40"
              >
                {serveTarget.saving ? "…" : "Servir"}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !deleteTarget.saving && setDeleteTarget(null)}>
          <form
            onSubmit={submitDelete}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl max-h-[95vh] overflow-y-auto"
            data-testid="public-order-delete-dialog"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="label-cap text-[var(--danger)]">eliminar pedido</div>
                <h3 className="font-display font-bold text-lg">¿Eliminar este pedido erróneo?</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  {deleteTarget.order.customer_name}{deleteTarget.order.table_number ? ` · Mesa ${deleteTarget.order.table_number}` : ""} · {deleteTarget.order.total.toFixed(2)} €
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-2">
                  Esta acción no se puede deshacer. Introduce tu contraseña para confirmar — quedará registrado quién lo eliminó.
                </p>
              </div>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleteTarget.saving} className="p-1 rounded hover:bg-[var(--bg)]" data-testid="public-order-delete-close">
                <X size={18} />
              </button>
            </div>
            <label className="label-cap block mb-2">Introduce tu contraseña</label>
            <PinPad
              value={deleteTarget.workerPin}
              onChange={(v) => setDeleteTarget((s) => ({ ...s, workerPin: v }))}
              maxLength={11}
              testIdPrefix="public-order-delete-pinpad"
              inputTestId="public-order-delete-pin"
              placeholder="••••"
            />
            {deleteTarget.error && (
              <div className="text-[var(--danger)] text-sm mt-2 text-center" data-testid="public-order-delete-error">{deleteTarget.error}</div>
            )}
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleteTarget.saving} className="flex-1 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={deleteTarget.saving || !deleteTarget.workerPin}
                data-testid="public-order-delete-submit"
                className="flex-1 py-3 rounded-xl bg-[var(--danger)] text-white font-display font-bold disabled:opacity-40"
              >
                {deleteTarget.saving ? "…" : "Eliminar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
